import {
  ObservationCategory,
  ObservationDataType,
  ServiceProviderObservationTypeModel,
} from '../../../../../api-service/clients';
import { Observation } from './observation-view-model';

// PBI 252889: sorted by category (Socialfaglige top, Generelle middle, Sundhedsfaglige bottom),
// alphabetically by name within each category.
const CATEGORY_ORDER: Record<ObservationCategory, number> = {
  [ObservationCategory.Social]: 0,
  [ObservationCategory.General]: 1,
  [ObservationCategory.Health]: 2,
};

const CATEGORY_LABELS: Record<ObservationCategory, string> = {
  [ObservationCategory.Social]: 'Socialfaglig',
  [ObservationCategory.General]: 'Generel',
  [ObservationCategory.Health]: 'Sundhedsfaglig',
};

const DATA_TYPE_LABELS: Record<ObservationDataType, string> = {
  [ObservationDataType.Integer]: 'Heltal',
  [ObservationDataType.Decimal]: 'Decimaltal',
  [ObservationDataType.Boolean]: 'Ja/Nej',
  [ObservationDataType.Text]: 'Tekst',
  [ObservationDataType.StandardTextDropdown]: 'Standardtekster',
};

export function categoryLabel(category: ObservationCategory): string {
  return CATEGORY_LABELS[category];
}

export function dataTypeLabel(dataType: ObservationDataType): string {
  return DATA_TYPE_LABELS[dataType];
}

export function sortObservationTypes(types: ServiceProviderObservationTypeModel[]): ServiceProviderObservationTypeModel[] {
  return [...types].sort((a, b) => {
    const byCategory = CATEGORY_ORDER[a.category] - CATEGORY_ORDER[b.category];
    if (byCategory !== 0) return byCategory;
    return (a.name ?? '').localeCompare(b.name ?? '', 'da');
  });
}

export function isNumericDataType(dataType: ObservationDataType): boolean {
  return dataType === ObservationDataType.Integer || dataType === ObservationDataType.Decimal;
}

export function isChartableAsPie(dataType: ObservationDataType): boolean {
  return dataType === ObservationDataType.Boolean || dataType === ObservationDataType.StandardTextDropdown;
}

export function isAvailableForUnit(type: ServiceProviderObservationTypeModel, serviceProviderUnitId: string | undefined): boolean {
  const unitIds = type.serviceProviderUnitIds ?? [];
  if (unitIds.length === 0) return true;
  return serviceProviderUnitId !== undefined && unitIds.includes(serviceProviderUnitId);
}

export function formatObservationValue(observation: Observation, type: ServiceProviderObservationTypeModel): string {
  if (isNumericDataType(type.dataType) && observation.numericValue !== undefined) {
    const formatted = type.dataType === ObservationDataType.Decimal
      ? observation.numericValue.toFixed(1).replace('.', ',')
      : String(observation.numericValue);
    return type.unit ? `${formatted} ${type.unit}` : formatted;
  }
  if (type.dataType === ObservationDataType.Boolean && observation.booleanValue !== undefined) {
    return observation.booleanValue ? 'Ja' : 'Nej';
  }
  return observation.textValue ?? '';
}

export function describeType(type: ServiceProviderObservationTypeModel): string {
  const parts: string[] = [categoryLabel(type.category), dataTypeLabel(type.dataType)];
  if (type.unit) parts.push(`enhed: ${type.unit}`);
  if (type.minValue != null && type.maxValue != null) {
    parts.push(`interval ${type.minValue}–${type.maxValue}`);
  }
  return parts.join(' · ');
}
